/**
 * 進貨管理列表(REQ-20260806002)。表格樣式沿用 .claude/skills/ui-guidelines §1。
 *
 * 每列的動作依狀態給。流程動作(送出確認 / 確認 / 退回草稿 / 刪除)是 REQ-20260813001
 * 的狀態機;編輯 / 作廢 / 列印是 REQ-20260814001 補上的,對齊出貨管理那張列表:
 *   DRAFT            送出確認 / 編輯 / 刪除
 *   PENDING_CONFIRM  確認 / 退回草稿
 *   POSTED           作廢(已付過款的改顯示「已付款,鎖定」,REQ-20260821001)
 *   VOIDED / 歷史單  不給任何動作
 * 列印不分狀態都在 —— 列印頁本來就是唯讀呈現。
 * REQ-20260911003 起「複製」同樣不分狀態都在(以該單為範本開新草稿),
 * 只有運輸費用類的單不給 —— 理由見那顆按鈕上方的註解。
 *
 * **編輯只給草稿、作廢只給已確認**都是後端既有的限制(R-08),不是這裡新加的:
 * 送出確認後單據凍結(要先退回草稿),而還沒進帳的單該用刪除而不是留一張作廢單。
 *
 * REQ-20260820006 起,篩選條件(進貨類別 / 關鍵字)受控於網址參數 —— 見下方 orderType。
 *
 * REQ-20260905004 起多一個「狀態」下拉,預設停在「全部狀態」——**語意是有效單據**
 * (草稿 + 待確認 + 已確認),不含已作廢 / 歷史單:那兩種日常用不到,混在一起版面太雜。
 * 要看時在同一個下拉裡選得到。作法與出貨管理的 REQ-20260820004 相同(後端展開,
 * 不在前端過濾 —— 清單只抓 50 筆,前端過濾會讓有效單據不足 50 張)。
 *
 * REQ-20260923001 起再多一個「到貨狀態」下拉(全部 / 未到貨 / 已到貨),用來回答
 * 「還有哪些貨還沒到」。篩的是 REQ-20260916001 那一欄 `receivedDate` 有沒有填,
 * 同樣下推給後端展開,**沒有新欄位、沒有新狀態**。選「未到貨」時「到貨日」那一格
 * (必然全空)改顯示等待天數;其餘情境一律維持原樣。
 *
 * REQ-20260907004 起,送出確認 / 確認兩顆要先跳 `ConfirmDialog` 才動作 —— 尤其是
 * 「確認」,它會計入應付並拋轉代管庫存,之後只剩作廢,付過款的連作廢都不行。
 * 退回草稿 / 刪除仍是原生 `window.prompt` / `window.confirm`(不在該次範圍)。
 *
 * REQ-20261013001 起,編輯表單儲存或取消後會捲回那一列,並在單號左側標一個橘色 ▶,
 * 留到下一次按編輯 / 複製 / 新增才清掉 —— 回到清單時才認得出剛剛改的是哪一張。
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PURCHASE_ORDER_STATUSES, type PurchaseOrderListItem } from '@app/shared';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import { referenceNameMap, useReferences } from '../settings/api';
import {
  useConfirmPurchaseOrder,
  useDeletePurchaseOrder,
  usePurchaseOrder,
  usePurchaseOrders,
  useReturnPurchaseOrderToDraft,
  useSubmitPurchaseOrder,
  useVoidPurchaseOrder,
} from '../accounting/api';
import { PurchaseOrderForm } from './PurchaseOrderForm';
import { isFreightCategory } from './work-order-category';

type SortKey = 'orderNo' | 'txnDate' | 'supplierSimpleName' | 'total' | 'outstanding';

/**
 * 狀態徽章配色(REQ-20260813001)。挑色的依據是「這張單還要不要人動手」:
 * 待確認是**唯一在等人做事**的狀態,所以給最顯眼的琥珀色;
 * 草稿灰(還沒進入流程)、已確認綠(進帳了)、作廢紅、歷史單藍(唯讀)。
 */
const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PENDING_CONFIRM: 'bg-amber-100 text-amber-800',
  POSTED: 'bg-green-100 text-green-700',
  VOIDED: 'bg-red-100 text-red-700',
  HISTORICAL: 'bg-blue-100 text-blue-700',
};

export function PurchaseOrdersPanel(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * 篩選條件放在網址上而不是本地 state(REQ-20260820006)——
   * 進表單再回來時清單才不會重置成「全部類別 + 沒有關鍵字」。
   * 空值就從網址拿掉;一律 `replace`,在搜尋框打字不該一個字一筆瀏覽紀錄。
   * 寫法沿用 CustomerConsolePage / SupplierConsolePage 既有的 useSearchParams 模式。
   */
  const orderType = searchParams.get('type') ?? '';
  const keyword = searchParams.get('q') ?? '';
  /** 預設「全部狀態」= 有效單據(REQ-20260905004),`ACTIVE` 由後端展開成三個狀態 */
  const status = searchParams.get('status') ?? 'ACTIVE';
  /**
   * 到貨狀態(REQ-20260923001)。預設空字串 = 不篩 —— 與「狀態」那個下拉不同,
   * 這裡沒有「日常只想看某一邊」的預設答案,進來還是先看全部。
   */
  const received = searchParams.get('received') ?? '';
  const setFilter = (key: 'type' | 'q' | 'status' | 'received', value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  /**
   * 正在被當成範本複製的那張單(REQ-20260911003)。與 `editingId` **互斥** ——
   * 表單區塊只有一個位置,同時開兩張只會讓人搞不清在改哪一張。
   */
  const [copyingId, setCopyingId] = useState<number | null>(null);
  /**
   * 剛剛編輯過(儲存或取消)的那張單(REQ-20261013001)。表單關掉、回到清單後,
   * 用來把畫面捲回那一列並在單號左側標一個橘色 ▶ —— 不然使用者會忘了剛剛改的是哪一張。
   * 一直留到下一次按「編輯」/「複製」/「+ 新增進貨單」才清掉;只放在記憶體,重新整理就消失。
   */
  const [lastEditedId, setLastEditedId] = useState<number | null>(null);
  /** 正在等待輸入作廢原因的那張單(REQ-20260814001,互動比照出貨管理) */
  const [voiding, setVoiding] = useState<{ id: number; orderNo: string } | null>(null);
  const [voidReason, setVoidReason] = useState('');
  /** 正在等待二次確認的流程動作(REQ-20260907004),null = 沒有視窗開著 */
  const [pendingAction, setPendingAction] = useState<{
    kind: 'submit' | 'confirm';
    id: number;
    orderNo: string;
  } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('txnDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, isError, error } = usePurchaseOrders({
    orderType: orderType || undefined,
    keyword: keyword.trim() || undefined,
    status,
    received: received || undefined,
  });
  /**
   * 進貨類別的 code → 中文(REQ-20260814002)。**取全部**(`all=true`):
   * 列表要顯示的是歷史單,其中可能有用「後來被停用的類別」開的單,
   * 只取啟用中的會讓那些單的類別退回顯示原始 code。
   * 篩選下拉則只列啟用中的 —— 停用的類別不該再被拿來篩新的查詢條件。
   */
  const { data: purchaseCategories } = useReferences('purchase-categories', true);
  const categoryNames = referenceNameMap(purchaseCategories?.data);
  const submitMutation = useSubmitPurchaseOrder();
  const confirmMutation = useConfirmPurchaseOrder();
  const returnMutation = useReturnPurchaseOrderToDraft();
  const deleteMutation = useDeletePurchaseOrder();
  const voidMutation = useVoidPurchaseOrder();
  /** 複製的來源明細(REQ-20260911003)。載入中 / 失敗的呈現與編輯模式同一套 */
  const copying = usePurchaseOrder(copyingId ?? undefined);
  const editing = usePurchaseOrder(editingId ?? undefined);
  const busy =
    submitMutation.isPending ||
    confirmMutation.isPending ||
    returnMutation.isPending ||
    deleteMutation.isPending;
  const [actionError, setActionError] = useState<string | null>(null);

  const runAction = (fn: () => void): void => {
    setActionError(null);
    setFlash(null);
    fn();
  };
  /**
   * 後端準備好的中文訊息在 `response.data.message`(axios 的 `error.message` 只有
   * 「Request failed with status code 400」)。作廢被擋下來的理由——已沖帳、
   * 狀態不對——正是使用者需要看到的那句話,所以走 `getApiErrorMessage()`。
   */
  const onActionError = (err: unknown): void => setActionError(getApiErrorMessage(err, '操作失敗'));

  /** 動作前先把上一輪的訊息清掉,免得成功訊息跟新的錯誤訊息同時掛在畫面上 */
  const startVoid = (target: { id: number; orderNo: string } | null): void => {
    setVoiding(target);
    setVoidReason('');
    setFlash(null);
    setActionError(null);
  };

  const confirmVoid = (target: { id: number; orderNo: string }): void => {
    const reason = voidReason.trim();
    if (!reason) {
      setActionError('請填寫作廢原因');
      return;
    }
    voidMutation.mutate(
      { id: target.id, reason },
      {
        onSuccess: () => {
          startVoid(null);
          setFlash(`${target.orderNo} 已作廢。`);
        },
        onError: onActionError,
      },
    );
  };

  /**
   * 二次確認按下「確定」之後才真的送出(REQ-20260907004)。內容是從那兩顆按鈕的
   * onClick 原封不動搬過來的 —— 搬家的理由是視窗掛在表格外面,拿不到那一列的 `o`。
   */
  const runPendingAction = (target: {
    kind: 'submit' | 'confirm';
    id: number;
    orderNo: string;
  }): void =>
    runAction(() => {
      if (target.kind === 'submit') {
        submitMutation.mutate(target.id, {
          onSuccess: () => setFlash(`${target.orderNo} 已送出確認,等待管理者確認後才會計入應付。`),
          onError: onActionError,
        });
        return;
      }
      confirmMutation.mutate(target.id, {
        onSuccess: (res) =>
          setFlash(
            res.managedInventory.length > 0
              ? `${target.orderNo} 已確認,並拋轉 ${res.managedInventory.length} 筆代管庫存(${res.managedInventory
                  .map((m) => `${m.itemName} ${m.quantity}`)
                  .join('、')})。`
              : `${target.orderNo} 已確認,已計入應付帳款(沒有超出出貨量的品項,未拋轉代管庫存)。`,
          ),
        onError: onActionError,
      });
    });

  /**
   * 開表單時把畫面捲到表單區塊(REQ-20260821004,與出貨管理同一套作法)。
   *
   * 表單一律渲染在畫面最上方,而清單可以很長 —— 使用者捲到中段按「編輯」,
   * 畫面停在原地不動,看起來就像按了沒反應。集中在這個 effect 而不是各個 onClick:
   * 編輯 / 新增最後都是改這兩個 state。`requestAnimationFrame` 是等表單那一輪畫完才捲
   * (寫法沿用 ReceivablePanel 的「查看收款沖帳紀錄」)。
   */
  useEffect(() => {
    if (editingId == null && copyingId == null && !showForm) return;
    requestAnimationFrame(() => {
      document
        .getElementById('purchase-order-form-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [editingId, copyingId, showForm]);

  /**
   * 編輯表單關掉後捲回剛剛那一列(REQ-20261013001)。`block: 'center'` 是讓那一列
   * 落在視窗中間、上下的單也看得到;找不到那一列(例如被篩選掉)就什麼都不做。
   */
  useEffect(() => {
    if (lastEditedId == null || editingId != null) return;
    requestAnimationFrame(() => {
      document
        .getElementById(`po-row-${lastEditedId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [lastEditedId, editingId]);

  const rows = useMemo(() => sortRows(data?.items ?? [], sortKey, sortDir), [data, sortKey, sortDir]);

  const onSort = (key: SortKey): void => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={keyword}
          onChange={(e) => setFilter('q', e.target.value)}
          placeholder="搜尋單號 / 廠商"
          className="border rounded-md px-3 py-1.5 text-sm"
        />
        <select
          value={orderType}
          onChange={(e) => setFilter('type', e.target.value)}
          aria-label="進貨類別"
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">全部類別</option>
          {(purchaseCategories?.data ?? [])
            .filter((c) => c.isActive)
            .map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
        </select>
        <select
          value={status}
          onChange={(e) => setFilter('status', e.target.value)}
          aria-label="狀態"
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="ACTIVE">全部狀態</option>
          {Object.entries(PURCHASE_ORDER_STATUSES).map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={received}
          onChange={(e) => setFilter('received', e.target.value)}
          aria-label="到貨狀態"
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">全部到貨狀態</option>
          <option value="PENDING">未到貨</option>
          <option value="RECEIVED">已到貨</option>
        </select>
        <button
          type="button"
          onClick={() => {
            setEditingId(null);
            setCopyingId(null);
            setLastEditedId(null);
            setShowForm((v) => !v);
          }}
          className="ml-auto bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
        >
          {showForm ? '取消' : '+ 新增進貨單'}
        </button>
      </div>

      {flash ? (
        <div role="status" className="text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 text-sm">
          {flash}
        </div>
      ) : null}

      {actionError ? (
        <div
          role="alert"
          className="text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm"
          data-testid="po-action-error"
        >
          {actionError}
        </div>
      ) : null}

      {/* 表單區塊的捲動目標(REQ-20260821004)。不論表單開不開都掛著,上面那個 effect
          才有固定的 id 可以捲過去;`empty:hidden` 是沒開表單時不要多佔一格 grid 間距。 */}
      <div id="purchase-order-form-section" className="empty:hidden">
        {copyingId != null ? (
          copying.isLoading ? (
            <div role="status" className="text-slate-500">
              載入來源單據中...
            </div>
          ) : copying.isError || !copying.data ? (
            <div role="alert" className="text-red-600">
              載入失敗：{getApiErrorMessage(copying.error, '未知錯誤')}
            </div>
          ) : (
            /* key 用來源 id:換一張來源單時整個表單要重新初始化,
               否則 useState 的初始值還停在上一張範本 */
            <PurchaseOrderForm
              key={`copy-${copying.data.id}`}
              copyFrom={copying.data}
              onCreated={(orderNo) => {
                const sourceOrderNo = copying.data?.orderNo ?? '';
                setCopyingId(null);
                setFlash(
                  `(複製自 ${sourceOrderNo})已建立進貨單 ${orderNo}(草稿)。接著按「送出確認」,由管理者確認後才會計入應付並拋轉代管庫存。`,
                );
              }}
              onCancel={() => setCopyingId(null)}
            />
          )
        ) : editingId != null ? (
          editing.isLoading ? (
            <div role="status" className="text-slate-500">
              載入單據明細中...
            </div>
          ) : editing.isError || !editing.data ? (
            <div role="alert" className="text-red-600">
              載入失敗：{getApiErrorMessage(editing.error, '未知錯誤')}
            </div>
          ) : (
            <PurchaseOrderForm
              key={editing.data.id}
              editOrder={editing.data}
              onCreated={(orderNo) => {
                setLastEditedId(editingId);
                setEditingId(null);
                setFlash(`進貨單 ${orderNo} 已儲存(仍是草稿)。`);
              }}
              onCancel={() => {
                setLastEditedId(editingId);
                setEditingId(null);
              }}
            />
          )
        ) : showForm ? (
          <PurchaseOrderForm
            onCreated={(orderNo) => {
              setShowForm(false);
              setFlash(
                `已建立進貨單 ${orderNo}(草稿)。接著按「送出確認」,由管理者確認後才會計入應付並拋轉代管庫存。`,
              );
            }}
            onCancel={() => setShowForm(false)}
          />
        ) : null}
      </div>

      {isLoading ? (
        <div role="status" className="text-slate-500">
          載入中...
        </div>
      ) : isError ? (
        <div role="alert" className="text-red-600">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm">
          {/* 篩了「未到貨」卻一張都沒有,是好消息而不是「還沒建過單」——
              原本那句會叫人去按「+ 新增進貨單」,方向完全相反(REQ-20260923001) */}
          {received === 'PENDING'
            ? '沒有未到貨的進貨單 —— 目前的單都已填上到貨日期。'
            : '目前沒有進貨單。按右上角「+ 新增進貨單」即可建立紙張採購或發外包加工。'}
        </div>
      ) : (
        <table className="w-full bg-white shadow-sm rounded-md">
          <thead>
            <tr className="border-b text-left text-sm text-slate-600">
              <SortableTh label="單號" k="orderNo" active={sortKey} dir={sortDir} onSort={onSort} />
              <SortableTh label="採購日" k="txnDate" active={sortKey} dir={sortDir} onSort={onSort} />
              {/* 到貨日(REQ-20260916001)。**不給排序** —— 它是選填的純紀錄欄位,
                  多數單是空的,拿它排序只會把沒補填的單全部堆在一頭;
                  預設排序仍然是採購日(`txnDate`) */}
              <th className="px-4 py-2">到貨日</th>
              <SortableTh label="廠商" k="supplierSimpleName" active={sortKey} dir={sortDir} onSort={onSort} />
              <th className="px-4 py-2">類別</th>
              <th className="px-4 py-2">代管客戶</th>
              <th className="px-4 py-2">狀態</th>
              <SortableTh label="含稅總計" k="total" active={sortKey} dir={sortDir} onSort={onSort} />
              <SortableTh label="未結" k="outstanding" active={sortKey} dir={sortDir} onSort={onSort} />
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <Fragment key={o.id}>
                {/* 剛剛編輯的那一列(REQ-20261013001):淡藍底 + 單號左側橘色 ▶。
                    刻意避開琥珀色(作廢確認列 / 待確認徽章)與灰色(排序箭頭);
                    hover 換深一階的藍,不用原本的 slate —— 否則滑過去標記底色就不見了。
                    使用者要求**不出現「剛剛編輯」的可見文字**,那幾個字只給讀屏軟體。
                    兩個 span 都 `select-none`:反白複製單號時才不會連「▶剛剛編輯」一起帶走。 */}
                <tr
                  id={`po-row-${o.id}`}
                  aria-current={o.id === lastEditedId ? 'true' : undefined}
                  className={`border-b last:border-0 text-sm ${
                    o.id === lastEditedId ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-slate-50'
                  }`}
                >
                  <td className="px-4 py-2 whitespace-nowrap font-mono">
                    {o.id === lastEditedId ? (
                      <>
                        <span
                          aria-hidden="true"
                          className="text-orange-500 mr-1 select-none"
                          data-testid={`po-last-edited-marker-${o.id}`}
                          title="剛剛編輯的單"
                        >
                          ▶
                        </span>
                        <span className="sr-only select-none">剛剛編輯</span>
                      </>
                    ) : null}
                    {o.orderNo}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{o.txnDate}</td>
                  {/* 沒補填的單留白(不用「—」)—— 那是「還沒到 / 還沒記」,
                      與代管客戶那種「這張單本來就不會有」不是同一件事。
                      **只有**篩「未到貨」時才改成等待天數(REQ-20260923001):
                      那個情境下整欄必然全空,留白等於什麼都沒回答;其餘情境
                      逐字維持 REQ-20260916001 的原樣 */}
                  <td className="px-4 py-2 whitespace-nowrap">
                    {o.receivedDate ??
                      (received === 'PENDING' ? (
                        <span
                          className="text-slate-500 text-xs"
                          data-testid={`po-waiting-days-${o.id}`}
                        >
                          已等 {waitingDays(o.txnDate)} 天
                        </span>
                      ) : (
                        ''
                      ))}
                  </td>
                  <td className="px-4 py-2 max-w-xs truncate">{o.supplierSimpleName}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {categoryNames[o.orderType] ?? o.orderType}
                  </td>
                  <td className="px-4 py-2 max-w-xs truncate">{o.customerSimpleName ?? '—'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                        STATUS_BADGE[o.status] ?? 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {PURCHASE_ORDER_STATUSES[o.status as keyof typeof PURCHASE_ORDER_STATUSES] ??
                        o.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right font-mono">
                    {formatMoney(o.total)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right font-mono">
                    {formatMoney(o.outstanding)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    <span className="inline-flex items-center gap-3">
                      {o.status === 'DRAFT' ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            setPendingAction({ kind: 'submit', id: o.id, orderNo: o.orderNo })
                          }
                          className="text-blue-600 hover:underline disabled:opacity-50"
                        >
                          送出確認
                        </button>
                      ) : null}
                      {o.status === 'PENDING_CONFIRM' ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            setPendingAction({ kind: 'confirm', id: o.id, orderNo: o.orderNo })
                          }
                          className="text-green-700 hover:underline disabled:opacity-50"
                        >
                          確認
                        </button>
                      ) : null}
                      {o.status === 'PENDING_CONFIRM' ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            const reason = window.prompt(`退回 ${o.orderNo} 的原因?`)?.trim();
                            if (!reason) return;
                            runAction(() =>
                              returnMutation.mutate(
                                { id: o.id, reason },
                                {
                                  onSuccess: () =>
                                    setFlash(`${o.orderNo} 已退回草稿,退回原因已寫進備註。`),
                                  onError: onActionError,
                                },
                              ),
                            );
                          }}
                          className="text-amber-700 hover:underline disabled:opacity-50"
                        >
                          退回草稿
                        </button>
                      ) : null}
                      {/* 編輯只給草稿:送出確認後單據凍結,要改得先退回草稿(後端擋,R-08) */}
                      {o.status === 'DRAFT' ? (
                        <button
                          type="button"
                          onClick={() => {
                            startVoid(null);
                            setShowForm(false);
                            setCopyingId(null);
                            setLastEditedId(null);
                            setEditingId(o.id);
                          }}
                          className="text-blue-600 hover:underline"
                          data-testid={`po-edit-btn-${o.id}`}
                        >
                          編輯
                        </button>
                      ) : null}
                      {/* 草稿只能刪不能作廢(R-08)—— 沒有這個按鈕的話草稿就成了死路 */}
                      {o.status === 'DRAFT' ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            if (!window.confirm(`確定刪除草稿 ${o.orderNo}?此動作無法復原。`)) return;
                            runAction(() =>
                              deleteMutation.mutate(o.id, {
                                onSuccess: () => setFlash(`草稿 ${o.orderNo} 已刪除。`),
                                onError: onActionError,
                              }),
                            );
                          }}
                          className="text-red-600 hover:underline disabled:opacity-50"
                        >
                          刪除
                        </button>
                      ) : null}
                      {/* 作廢只給已確認的單 —— 後端只放行 POSTED,給別的狀態按了只會拿到 400。
                          歷史單(mdb 匯入)也不給:出貨管理那邊給,是因為出貨的 void() 收 HISTORICAL */}
                      {/* 已付過款的單在畫面上直接鎖死(REQ-20260821001),與出貨管理同一條規則。
                          後端的守門沒有拿掉,前端只是不給入口。REQ-20261029002 補上應付側的
                          解除沖銷(只在廠商頁「應付沖帳」分頁),提示比照出貨管理指路過去;
                          解除後已付淨額回到 0,這裡自動解鎖。 */}
                      {o.status === 'POSTED' && Number(o.settledAmount) > 0 ? (
                        <span
                          className="text-slate-400"
                          title="這張進貨單已經付過款,不能作廢。要作廢請先到廠商頁「應付沖帳」解除沖銷。"
                          data-testid={`po-locked-${o.id}`}
                        >
                          已付款,鎖定
                        </span>
                      ) : null}
                      {o.status === 'POSTED' && !(Number(o.settledAmount) > 0) ? (
                        <button
                          type="button"
                          onClick={() => startVoid({ id: o.id, orderNo: o.orderNo })}
                          className="text-red-600 hover:underline"
                          data-testid={`po-void-btn-${o.id}`}
                        >
                          作廢
                        </button>
                      ) : null}
                      {/* 複製:以這張單當範本開一張新草稿(REQ-20260911003)。
                          **不分狀態都給**(比照列印)—— 經常性採購要拿來複製的
                          通常正是已確認或更早的歷史單,只給草稿等於沒做。
                          唯一的例外是運輸費用類:那種單的明細由運費歸單自動產生、
                          每一列掛著唯一的來源運費明細 id(REQ-20260908002),
                          複製出來會是一張對不上任何來源的假單。 */}
                      {!isFreightCategory(categoryNames[o.orderType]) ? (
                        <button
                          type="button"
                          onClick={() => {
                            startVoid(null);
                            setShowForm(false);
                            setEditingId(null);
                            setLastEditedId(null);
                            setCopyingId(o.id);
                          }}
                          className="text-blue-600 hover:underline"
                          data-testid={`po-copy-btn-${o.id}`}
                        >
                          複製
                        </button>
                      ) : null}
                      {/* 列印不分狀態都給(比照出貨管理),列印頁本來就是唯讀呈現 */}
                      <Link
                        to={`/purchase-orders/${o.id}/print`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                        data-testid={`po-print-link-${o.id}`}
                      >
                        列印
                      </Link>
                    </span>
                  </td>
                </tr>

                {voiding?.id === o.id ? (
                  <tr className="border-b last:border-0 bg-amber-50 text-sm">
                    <td colSpan={10} className="px-4 py-2">
                      <div
                        className="flex flex-wrap items-center justify-end gap-2"
                        data-testid={`po-void-confirm-${o.id}`}
                      >
                        <label className="mr-auto flex items-center gap-2 text-slate-700">
                          作廢 {o.orderNo} 的原因
                          <input
                            type="text"
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            className="border rounded-md px-2 py-1 text-sm"
                            data-testid={`po-void-reason-${o.id}`}
                          />
                        </label>
                        <button
                          type="button"
                          disabled={voidMutation.isPending}
                          onClick={() => confirmVoid(voiding)}
                          className="text-red-600 hover:underline disabled:opacity-50"
                        >
                          確定作廢
                        </button>
                        <button
                          type="button"
                          onClick={() => startVoid(null)}
                          className="text-slate-600 hover:underline"
                        >
                          取消
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/*
        送出確認 / 確認的二次確認(REQ-20260907004)。兩顆按鈕的結果差很多,所以文案
        分開寫實際會發生的事:送出確認只是凍結單據(還能退回草稿),真正計入應付並
        拋轉代管庫存的是「確認」——「確認」之後就只剩作廢,付過款的連作廢都不行,
        所以只有它給紅色的 danger 樣式。
        取消 / ESC / 點背景都只走 onClose,不呼叫任何 mutation,單據留在原地。
      */}
      <ConfirmDialog
        open={pendingAction !== null}
        title={pendingAction?.kind === 'confirm' ? '確認並過帳' : '送出確認'}
        message={
          pendingAction?.kind === 'confirm' ? (
            <>
              確定要確認進貨單 <span className="font-mono font-semibold">{pendingAction.orderNo}</span>{' '}
              嗎?確認後系統會自動計入應付帳款並拋轉代管庫存,之後只能作廢,已付款的單連作廢都不行。
            </>
          ) : (
            <>
              確定要將進貨單{' '}
              <span className="font-mono font-semibold">{pendingAction?.orderNo}</span>{' '}
              送出確認嗎?送出後單據會凍結、不能再修改,要改得先退回草稿。此步驟還不會產生應付帳款。
            </>
          )
        }
        confirmLabel={pendingAction?.kind === 'confirm' ? '確定確認' : '確定送出'}
        cancelLabel="取消"
        tone={pendingAction?.kind === 'confirm' ? 'danger' : 'primary'}
        onConfirm={() => {
          if (pendingAction) runPendingAction(pendingAction);
        }}
        onClose={() => setPendingAction(null)}
      />
    </div>
  );
}

function SortableTh({
  label,
  k,
  active,
  dir,
  onSort,
}: {
  label: string;
  k: SortKey;
  active: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}): JSX.Element {
  const isActive = active === k;
  return (
    <th className="px-4 py-2">
      <button
        type="button"
        onClick={() => onSort(k)}
        className="inline-flex items-center gap-1 hover:text-blue-600"
        aria-label={`依${label}排序`}
      >
        {label}
        <span className={isActive ? 'text-blue-600 text-xs' : 'text-slate-300 text-xs'}>
          {isActive && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

/**
 * 這張單從採購日等到今天幾天了(REQ-20260923001)。只在「未到貨」篩選下顯示。
 *
 * ⚠️ 今天一定要用 **Asia/Taipei**:`toISOString().slice(0, 10)` 取到的是 UTC 日期,
 * 台灣時間 00:00-08:00 看會少一天(CLAUDE.md §6.1)。寫法沿用 PurchaseOrderForm 的
 * `todayInTaipei()`。兩個日期都已經是 `YYYY-MM-DD` 字串,用 `Date.UTC` 相減,
 * 免得再被本地時區污染一次。
 *
 * 採購日在未來(預先建的單)會算成負數 —— 那不是「等了負幾天」,一律顯示 0。
 */
function waitingDays(txnDate: string): number {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const diff = (ymdToUtc(today) - ymdToUtc(txnDate)) / 86_400_000;
  return diff > 0 ? diff : 0;
}

/** `YYYY-MM-DD` → UTC 毫秒。用 `slice` 不用 `split`,省掉索引可能是 undefined 的處理 */
function ymdToUtc(ymd: string): number {
  return Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)));
}

function sortRows(
  items: PurchaseOrderListItem[],
  key: SortKey,
  dir: 'asc' | 'desc',
): PurchaseOrderListItem[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    if (key === 'total' || key === 'outstanding') {
      return (Number(a[key]) - Number(b[key])) * sign;
    }
    return a[key].localeCompare(b[key], 'zh-Hant') * sign;
  });
}
